import { AsyncPipe, CommonModule, NgForOf, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { RouterLink } from "@angular/router";
import { TuiDay, TuiValidationError } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialogService,
  TuiDropdown,
  TuiError,
  TuiLink,
  TuiTextfield,
  TuiLoader
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListWrapper,
  TuiFade,
  TuiFieldErrorPipe,
  TuiFiles,
  tuiFilesAccepted,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTabs,
  tuiValidationErrorsProvider,
} from "@taiga-ui/kit";
import { TuiNavigation } from "@taiga-ui/layout";
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { catchError, map, of } from "rxjs";
import { FileService } from "../../../core/services/file.service";
import { RolesService } from "../../../core/services/roles.service";
import { FeeService } from "../../../core/services/fee.service";
import { Breadcrumb } from "../../../model/interface/breadcrumb";
import { ClientService } from "../../../core/services/client.service";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";
import { EnterSubmitDirective } from "../../../core/directives/enter-submit.directive";
import {
  clientIdentifierValidator,
  nameValidator,
  numericValidator as sharedNumericValidator,
  phoneValidator,
} from "../../../shared/validators/form-validators";
import {
  countInvalidFields,
  focusFirstInvalid,
} from "../../../shared/utils/form-focus";
import { RecommendedHintComponent } from "../../../shared/components/recommended-hint/recommended-hint.component";
import { normalizeClientIdentifier } from "../../../shared/utils/client-identifier";
import {
  clearClientTypeConvertSession,
  readClientTypeConvertSession,
} from "../../../shared/utils/client-type-conversion.storage";

export const CLIENT_TYPES = {
  REGISTERED: "Registered",
  UNREGISTERED: "Unregistered",
  INWARD: "Inward",
} as const;

export const NURSING_TYPES = {
  WITH_HOME_NURSING: "With Home Nursing",
  WITHOUT_HOME_NURSING: "Without Home Nursing",
} as const;

export const INWARD_MEAL_OPTIONS = ["With Meal", "Without Meal"];
export const INWARD_ROSTER_OPTIONS = ["12 Hours", "24 Hours"];

@Component({
  selector: "app-add-client",
  imports: [
    DisableUnlessPermissionDirective,
    EnterSubmitDirective,
    FormsModule,
    CommonModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiFade,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiFiles,
    TuiLoader,
    TuiFieldErrorPipe,
    TuiError,
    ReactiveFormsModule,
    AsyncPipe,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiTextareaModule,
    TuiInputPhoneModule,
    TuiComboBoxModule,
    TuiMultiSelectModule,
    TuiSelectModule,
    TuiDataListWrapper,
    TuiFilterByInputPipe,
    TuiStringifyContentPipe,
    RouterLink,
    TuiInputDateModule,
    RecommendedHintComponent,
  ],
  templateUrl: "./add-client.component.html",
  styleUrl: "./add-client.component.scss",
  providers: [
    tuiValidationErrorsProvider({
      duplicateEmail: "Already existing!",
      duplicate: "Already existing!",
      invalidIdentifier:
        "Invalid ID. Use digits only (e.g. 0001) or full format REG-0001 / NREG-0001 / BHT-00001.",
    }),
  ],
})
export class AddClientComponent {
  breadcrumbs: Breadcrumb[] = [
    { label: "Royal Hospital" },
    { label: "Clients", link: "admin/client" },
    { label: "Add New Client" },
  ];

  // Expose constants to template
  readonly CLIENT_TYPES = CLIENT_TYPES;
  readonly NURSING_TYPES = NURSING_TYPES;
  readonly clientTypeOptions: string[] = [
    CLIENT_TYPES.REGISTERED,
    CLIENT_TYPES.UNREGISTERED,
    CLIENT_TYPES.INWARD,
  ];

  /** Display label for client types (UI-only). */
  readonly stringifyClientType = (value: string | null): string => {
    if (!value) return "";
    if (value === CLIENT_TYPES.UNREGISTERED) return "Non Registered";
    return value;
  };
  readonly nursingTypeOptions: string[] = [
    NURSING_TYPES.WITH_HOME_NURSING,
    NURSING_TYPES.WITHOUT_HOME_NURSING,
  ];
  readonly mealOptions: string[] = INWARD_MEAL_OPTIONS;
  readonly rosterOptions: string[] = INWARD_ROSTER_OPTIONS;

  protected value = false;
  clientId: string | null = null;
  imageEditable: boolean = false;
  /**
   * Soft-warn flexibility: in edit mode the email is disabled by default
   * (it's the login identifier for Registered clients), but clicking the
   * pencil unlocks it so typos can be fixed. Backend still enforces the
   * uniqueness check on save.
   */
  emailEditUnlocked: boolean = false;
  /**
   * Same idea for the registration / renewal fee inputs - we auto-fill
   * the system fee, lock the inputs to prevent accidental edits, but
   * allow an explicit "Override fee" toggle for legitimate corrections.
   */
  feeEditUnlocked: boolean = false;
  clientForm: FormGroup;
  isLoading: boolean = false;
  private isClientDataLoading: boolean = false;
  private isFeesLoading: boolean = false;
  newClient: boolean = true;
  image: string | null = null;
  roles: any[] = [];
  dropDown: FormControl;
  imagePreview: string | ArrayBuffer | null = null;
  registrationOptions: string[] = ["Life Time", "One Year"];
  private returnUrl: string | null = null;
  currentRegistrationFee: number = 0;
  currentRenewalFee: number = 0;
  currentLifetimeRegistrationFee: number = 0;
  clientRegistrationFee: number | null = null;
  /** Snapshot of registration type from the server when edit form loads. */
  private originalRegistrationType: string | null = null;
  originalExpiryDate: TuiDay | null = null;
  originalRenewalDate: TuiDay | null = null;
  latestRenewalFee: number = 0;
  /** Renewal fee from server when edit form loads (for override detection). */
  private originalLatestRenewalFee: number | null = null;
  /** UI: last + suggested next identifier for the selected clientType (new only). */
  lastClientId: string | null = null;
  suggestedClientId: string | null = null;
  /** Set when creating a client after a type-change archive flow. */
  convertedFromClientId: string | null = null;
  conversionBannerSummary: string | null = null;
  stringify = (role: any) => role.name;
  private readonly dialogs = inject(TuiDialogService);

  constructor(
    private fb: FormBuilder,
    private clientService: ClientService,
    private router: Router,
    private alerts: TuiAlertService,
    private route: ActivatedRoute,
    private filesService: FileService,
    private rolesService: RolesService,
    private feeService: FeeService
  ) {
    this.dropDown = this.fb.control(null);
    this.clientForm = this.fb.group({
      _id: [null],
      // Type & identifiers
      clientType: [CLIENT_TYPES.REGISTERED, Validators.required],
      registrationNo: [""],
      nonRegNo: [""],
      bhtNumber: [""],

      // Common (Patient Name) - required for all
      name: ["", [Validators.required]],

      // Registered + Unregistered fields. Per-type required wiring lives in
      // applyValidatorsForType below; the form-level validators are just
      // sensible base validators (no required, max length, format).
      age: [""],
      ageMonths: [""],
      guardianName: [""],
      guardianNIC: [""],
      guardianContactNo: ["", [Validators.maxLength(20), phoneValidator]],
      guardianAddress: [""],
      email: [""],
      dateOfBirth: [null],
      registrationType: [""],
      expiryDate: [null],
      renewalDate: [null],
      registrationFee: [null],
      renewalFee: [null],
      currentLocation: [""],
      registrationDate: [null],
      active: [true],
      currentCondition: [""],

      // Unregistered specific
      nursingType: [""],
      startingDate: [null],

      // Inward specific
      roomNumber: [""],
      meal: [""],
      roster: [""],
      inwardDate: [null],
    });
  }

  navigate(link?: string): void {
    if (link) {
      this.router.navigate([link]);
    }
  }

  ngOnInit() {
    this.returnUrl = this.route.snapshot.queryParamMap.get("returnUrl");
    const fromConversion =
      this.route.snapshot.queryParamMap.get("fromConversion") === "1";

    this.route.paramMap.subscribe((params) => {
      this.clientId = params.get("id");
      if (this.clientId) {
        this.newClient = false;
        this.loadUserData(this.clientId);
        this.imageEditable = true;
        this.breadcrumbs = [
          { label: "Royal Hospital" },
          { label: "Clients", link: "admin/client" },
          { label: "Edit Client" },
        ];
      } else {
        this.newClient = true;
        this.originalRegistrationType = null;
        this.clientRegistrationFee = null;
        if (fromConversion) {
          this.applyConversionPrefill();
        }
      }
      // Set up date calculations and listeners for both new and edit modes
      this.setupExpiryDateCalculation();
    });
    this.loadRoles();
    this.loadCurrentFees();

    // React to client type changes - apply per-type validators
    this.clientForm.get("clientType")?.valueChanges.subscribe((type) => {
      this.applyValidatorsForType(type);
      if (this.newClient) {
        this.loadIdSuggestion(type);
      }
    });
    // Apply initial validators based on default type
    this.applyValidatorsForType(this.clientForm.get("clientType")?.value);
    if (this.newClient) {
      this.loadIdSuggestion(this.clientForm.get("clientType")?.value);
    }
  }

  private checkLoadingState(): void {
    this.isLoading = this.isClientDataLoading || this.isFeesLoading;
  }


  private applyConversionPrefill(): void {
    const session = readClientTypeConvertSession();
    clearClientTypeConvertSession();
    if (!session?.prefill || !session.targetType) return;

    this.convertedFromClientId = session.archivedClientId;
    this.conversionBannerSummary =
      session.archivedClientSummary || session.archivedClientId;

    const prefill = { ...session.prefill };
    const dateFields = [
      "registrationDate",
      "dateOfBirth",
      "expiryDate",
      "renewalDate",
      "startingDate",
      "inwardDate",
    ] as const;
    for (const field of dateFields) {
      if (prefill[field]) {
        prefill[field] = TuiDay.fromLocalNativeDate(
          new Date(String(prefill[field])),
        );
      }
    }

    this.clientForm.patchValue({
      ...prefill,
      clientType: session.targetType,
      _id: null,
    });

    this.applyValidatorsForType(session.targetType);
    this.loadIdSuggestion(session.targetType);
    this.applyFeesBasedOnType();

    if (prefill["profilePic"]) {
      this.imagePreview = String(prefill["profilePic"]);
    }

    this.breadcrumbs = [
      { label: "Royal Hospital" },
      { label: "Clients", link: "admin/client" },
      { label: "New client (type change)" },
    ];
  }

  private loadIdSuggestion(type: string, autoFill = true): void {
    if (!type) return;
    this.clientService.getIdSuggestion(type).subscribe({
      next: (res: any) => {
        const data = res?.data;
        this.lastClientId = data?.lastId ?? null;
        this.suggestedClientId = data?.nextId ?? null;

        // Auto-fill the identifier for new clients if empty.
        if (!autoFill || !this.newClient || !this.suggestedClientId) return;
        if (type === CLIENT_TYPES.REGISTERED) {
          const ctrl = this.clientForm.get("registrationNo");
          if (ctrl && !ctrl.value) ctrl.setValue(this.suggestedClientId);
        } else if (type === CLIENT_TYPES.UNREGISTERED) {
          const ctrl = this.clientForm.get("nonRegNo");
          if (ctrl && !ctrl.value) ctrl.setValue(this.suggestedClientId);
        } else if (type === CLIENT_TYPES.INWARD) {
          const ctrl = this.clientForm.get("bhtNumber");
          if (ctrl && !ctrl.value) ctrl.setValue(this.suggestedClientId);
        }
      },
      error: () => {
        this.lastClientId = null;
        this.suggestedClientId = null;
      },
    });
  }

  useSuggestedId(): void {
    const type = this.clientForm.get("clientType")?.value;
    if (!this.suggestedClientId || !type) return;
    if (type === CLIENT_TYPES.REGISTERED) {
      this.clientForm.get("registrationNo")?.setValue(this.suggestedClientId);
    } else if (type === CLIENT_TYPES.UNREGISTERED) {
      this.clientForm.get("nonRegNo")?.setValue(this.suggestedClientId);
    } else if (type === CLIENT_TYPES.INWARD) {
      this.clientForm.get("bhtNumber")?.setValue(this.suggestedClientId);
    }
  }

  /**
   * Toggle Validators on/off based on the chosen client type so the form
   * stays valid for whichever subset of fields is visible.
   */
  applyValidatorsForType(type: string): void {
    const setRequired = (name: string, required: boolean, extra: any[] = []) => {
      const ctrl = this.clientForm.get(name);
      if (!ctrl) return;
      const validators = required ? [Validators.required, ...extra] : extra;
      ctrl.setValidators(validators);
      if (!required) ctrl.setErrors(null);
      ctrl.updateValueAndValidity({ emitEvent: false });
    };

    // Soft-warn philosophy: only keep `Validators.required` on the small set
    // of fields that the system genuinely cannot operate without. Everything
    // else stays in the form (visible, persisted) but renders an
    // <app-recommended-hint> instead of blocking the save.
    if (type === CLIENT_TYPES.REGISTERED) {
      // Hard required: identifier, registration type (drives expiry/fee),
      // patient name (already at the form level), guardian name + contact.
      setRequired("registrationNo", true, [
        clientIdentifierValidator(CLIENT_TYPES.REGISTERED),
      ]);
      setRequired("registrationType", true);
      setRequired("guardianName", true);
      setRequired("guardianContactNo", true, [Validators.maxLength(20), phoneValidator]);

      // Demoted (recommended, not required):
      setRequired("age", false, [sharedNumericValidator]);
      setRequired("ageMonths", false, [sharedNumericValidator]);
      setRequired("guardianNIC", false);
      setRequired("guardianAddress", false);
      setRequired("email", false, [Validators.email]);
      setRequired("dateOfBirth", false);
      setRequired("registrationFee", false);
      setRequired("registrationDate", false);
      setRequired("currentCondition", false);

      // Cleared (not used by Registered):
      setRequired("nonRegNo", false);
      setRequired("bhtNumber", false);
      setRequired("nursingType", false);
      setRequired("startingDate", false);
      setRequired("roomNumber", false);
      setRequired("meal", false);
      setRequired("roster", false);
      setRequired("inwardDate", false);
    } else if (type === CLIENT_TYPES.UNREGISTERED) {
      // Hard required: non-reg identifier, nursing type (billing key),
      // starting date, guardian name + contact.
      setRequired("nonRegNo", true, [
        clientIdentifierValidator(CLIENT_TYPES.UNREGISTERED),
      ]);
      setRequired("nursingType", true);
      setRequired("startingDate", true);
      setRequired("guardianName", true);
      setRequired("guardianContactNo", true, [Validators.maxLength(20), phoneValidator]);

      // Demoted:
      setRequired("guardianAddress", false);
      setRequired("age", false, [sharedNumericValidator]);
      setRequired("ageMonths", false, [sharedNumericValidator]);
      setRequired("guardianNIC", false);
      setRequired("email", false, [Validators.email]);
      setRequired("dateOfBirth", false);
      setRequired("currentCondition", false);

      // Cleared:
      setRequired("registrationNo", false);
      setRequired("bhtNumber", false);
      setRequired("registrationType", false);
      setRequired("registrationFee", false);
      setRequired("registrationDate", false);
      setRequired("roomNumber", false);
      setRequired("meal", false);
      setRequired("roster", false);
      setRequired("inwardDate", false);
    } else if (type === CLIENT_TYPES.INWARD) {
      // Hard required (per plan): just BHT number + inward date. Patient
      // name is already required at the form level. Everything else for
      // Inward is recommended.
      setRequired("bhtNumber", true, [
        clientIdentifierValidator(CLIENT_TYPES.INWARD),
      ]);
      setRequired("inwardDate", true);

      // Demoted (still routinely captured but no longer blocking):
      setRequired("roomNumber", false);
      setRequired("meal", false);
      setRequired("roster", false);
      setRequired("guardianName", false);
      setRequired("guardianContactNo", false, [Validators.maxLength(20), phoneValidator]);
      setRequired("age", false, [sharedNumericValidator]);
      setRequired("ageMonths", false, [sharedNumericValidator]);
      setRequired("guardianNIC", false);
      setRequired("guardianAddress", false);
      setRequired("email", false, [Validators.email]);
      setRequired("dateOfBirth", false);
      setRequired("currentCondition", false);

      // Cleared:
      setRequired("registrationNo", false);
      setRequired("nonRegNo", false);
      setRequired("registrationType", false);
      setRequired("registrationFee", false);
      setRequired("registrationDate", false);
      setRequired("nursingType", false);
      setRequired("startingDate", false);
    }
  }

  setupExpiryDateCalculation() {
    this.clientForm
      .get("registrationType")
      ?.valueChanges.subscribe((regType) => {
        if (regType === "One Year") {
          const regDate = this.clientForm.get("registrationDate")?.value;
          let calculatedExpiryDate;
          if (regDate) {
            const nativeDate =
              regDate instanceof Date ? regDate : regDate.toLocalNativeDate();
            const expiry = new Date(nativeDate);
            expiry.setFullYear(expiry.getFullYear() + 1);
            calculatedExpiryDate = TuiDay.fromLocalNativeDate(expiry);
          } else {
            const expiry = new Date();
            expiry.setFullYear(expiry.getFullYear() + 1);
            calculatedExpiryDate = TuiDay.fromLocalNativeDate(expiry);
          }
          this.clientForm.get("expiryDate")?.setValue(calculatedExpiryDate);
        } else if (regType === "Life Time") {
          this.clientForm.get("expiryDate")?.setValue(null);
        }

        // Update fees based on registration type
        this.applyFeesBasedOnType();
      });

    this.clientForm
      .get("registrationDate")
      ?.valueChanges.subscribe((regDate) => {
        const regType = this.clientForm.get("registrationType")?.value;
        if (regType === "One Year" && regDate) {
          const nativeDate =
            regDate instanceof Date ? regDate : regDate.toLocalNativeDate();
          const expiry = new Date(nativeDate);
          expiry.setFullYear(expiry.getFullYear() + 1);
          const calculatedExpiryDate = TuiDay.fromLocalNativeDate(expiry);
          this.clientForm.get("expiryDate")?.setValue(calculatedExpiryDate);
        }
      });

    // Track renewal date changes to apply system renewal fee with confirmation
    this.clientForm
      .get("renewalDate")
      ?.valueChanges.subscribe((newRenewalDate) => {
        const regType = this.clientForm.get("registrationType")?.value;

        // For One Year clients, always update expiry to renewal + 1 year
        if (regType === "One Year" && newRenewalDate) {
          try {
            const native = newRenewalDate instanceof Date ? newRenewalDate : newRenewalDate.toLocalNativeDate();
            const expiry = new Date(native);
            expiry.setFullYear(expiry.getFullYear() + 1);
            this.clientForm.get('expiryDate')?.setValue(TuiDay.fromLocalNativeDate(expiry), { emitEvent: false });
          } catch (e) {
            console.error('Error updating expiry date:', e);
          }
        }

      });
  }

  // Helper method to compare TuiDay dates
  private areDatesEqual(date1: TuiDay | null, date2: TuiDay | null): boolean {
    if (!date1 || !date2) return false;
    return date1.day === date2.day &&
      date1.month === date2.month &&
      date1.year === date2.year;
  }

  editImage() {
    this.imageEditable = false;
  }

  /**
   * Unlock the email control in edit mode so the user can correct a typo.
   * Re-applies the email format validator. Backend uniqueness check stays.
   */
  unlockEmailEdit(): void {
    this.emailEditUnlocked = true;
    const ctrl = this.clientForm.get("email");
    ctrl?.enable();
    ctrl?.setValidators([Validators.email]);
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Toggle the registration/renewal fee inputs between locked (showing the
   * current system fee) and unlocked (manual override). When unlocking we
   * just enable; when re-locking we restore the system fee values.
   */
  toggleFeeEdit(): void {
    this.feeEditUnlocked = !this.feeEditUnlocked;
    const regFee = this.clientForm.get("registrationFee");
    const renewFee = this.clientForm.get("renewalFee");
    if (this.feeEditUnlocked) {
      regFee?.enable({ emitEvent: false });
      renewFee?.enable({ emitEvent: false });
    } else {
      this.applyFeesBasedOnType();
      regFee?.disable({ emitEvent: false });
      renewFee?.disable({ emitEvent: false });
    }
  }

  loadUserData(clientId: string): void {
    this.isClientDataLoading = true;
    this.isLoading = true;
    this.clientService
      .getOneClientById(clientId)
      .pipe(
        catchError((error) => {
          console.error("Error loading client data:", error);
          this.isClientDataLoading = false;
          this.checkLoadingState();
          return of(null);
        })
      )
      .subscribe(
        (
          response: {
            registrationDate?: TuiDay;
            dateOfBirth?: TuiDay;
            expiryDate?: TuiDay;
            renewalDate?: TuiDay;
            startingDate?: TuiDay;
            inwardDate?: TuiDay;
            registrationType?: string;
            renewalFees?: { date: Date; fee: number }[];
            [key: string]: any;
          } | null
        ) => {
          const rawAny: any = (response as any)?.data ? (response as any).data : response;
          if (rawAny) {
            const response: any = { ...rawAny };

            // Some legacy rows may be missing clientType; infer from identifiers.
            if (!response.clientType) {
              if (response.nonRegNo) response.clientType = CLIENT_TYPES.UNREGISTERED;
              else if (response.bhtNumber) response.clientType = CLIENT_TYPES.INWARD;
              else response.clientType = CLIENT_TYPES.REGISTERED;
            }
            (
              [
                "registrationDate",
                "dateOfBirth",
                "expiryDate",
                "renewalDate",
                "startingDate",
                "inwardDate",
              ] as const
            ).forEach((field) => {
              if (response[field]) {
                response[field] = TuiDay.fromLocalNativeDate(
                  new Date(response[field]!.toString())
                );
              }
            });
            // Store original expiryDate from DB
            this.originalExpiryDate = response.expiryDate || null;

            // Use the renewalDate directly from the client record, not from array
            this.originalRenewalDate = response.renewalDate || null;

            // Get the latest renewal fee from renewalFees array if it exists
            if (response.renewalFees && response.renewalFees.length > 0) {
              const latestRenewal = response.renewalFees.reduce((latest: any, current: any) => {
                const currentDate = new Date(current.date);
                const latestDate = new Date(latest.date);
                return currentDate > latestDate ? current : latest;
              });
              this.latestRenewalFee = latestRenewal.fee || 0;
            } else {
              this.originalRenewalDate = response.renewalDate || null;
            }

            // Capture client's stored registrationFee (if any) and patch other fields
            const { registrationFee, renewalFees, ...rest } = response as any;
            this.clientRegistrationFee =
              typeof registrationFee === 'number' ? registrationFee : null;
            this.originalRegistrationType =
              typeof rest.registrationType === 'string' &&
                String(rest.registrationType).trim() !== ''
                ? rest.registrationType
                : null;
            this.clientForm.patchValue(rest);

            // Apply validators based on the loaded clientType
            this.applyValidatorsForType(this.clientForm.get("clientType")?.value);

            this.loadIdSuggestion(
              this.clientForm.get("clientType")?.value,
              false,
            );

            // Email should only be locked for Registered clients (login identifier).
            const loadedType = this.clientForm.get("clientType")?.value;
            const emailCtrl = this.clientForm.get("email");
            if (loadedType === CLIENT_TYPES.REGISTERED && !this.emailEditUnlocked) {
              emailCtrl?.disable({ emitEvent: false });
            } else {
              emailCtrl?.enable({ emitEvent: false });
            }

            // For edit mode with One Year type, display the latest renewal fee
            if (!this.newClient && this.clientForm.get('registrationType')?.value === 'One Year' && this.latestRenewalFee > 0) {
              this.clientForm.patchValue({ renewalFee: this.latestRenewalFee });
            }

            // For edit mode: set expiry date to 1 year from renewal date for One Year clients
            if (!this.newClient && this.clientForm.get('registrationType')?.value === 'One Year') {
              const renewal = this.clientForm.get('renewalDate')?.value;
              if (renewal) {
                try {
                  const native = renewal instanceof Date ? renewal : renewal.toLocalNativeDate();
                  const expiry = new Date(native);
                  expiry.setFullYear(expiry.getFullYear() + 1);
                  this.clientForm.get('expiryDate')?.setValue(TuiDay.fromLocalNativeDate(expiry));
                } catch (e) {
                  // ignore invalid date
                }
              }
            }

            // Reapply fees based on the loaded registration type. applyFeesBasedOnType
            // will prefer client-stored registrationFee/renewalFee when present.
            this.applyFeesBasedOnType();

            const loadedRenewalFee = this.clientForm.get("renewalFee")?.value;
            this.originalLatestRenewalFee =
              loadedRenewalFee != null && loadedRenewalFee !== ""
                ? Number(loadedRenewalFee)
                : this.latestRenewalFee > 0
                  ? this.latestRenewalFee
                  : null;
          }
          this.isClientDataLoading = false;
          this.checkLoadingState();
        }
      );
  }

  loadRoles(): void {
    this.rolesService.getRolesForOptions().subscribe(
      (response) => {
        this.roles = response as any;
      },
      (error) => {
        console.error("Error fetching roles:", error);
      }
    );
  }

  loadCurrentFees(): void {
    this.isFeesLoading = true;
    this.isLoading = true;
    this.feeService.getCurrentFee().subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          this.currentRegistrationFee = res.data.registrationFee || 0;
          this.currentRenewalFee = res.data.renewalFee || 0;
          this.currentLifetimeRegistrationFee = res.data.lifetimeRegistrationFee || 0;

          // Apply appropriate fee based on registration type
          this.applyFeesBasedOnType();

          // Lock the controls by default; the "Override fee" toggle in the
          // template re-enables them via toggleFeeEdit().
          if (!this.feeEditUnlocked) {
            this.clientForm.get('registrationFee')?.disable({ emitEvent: false });
            this.clientForm.get('renewalFee')?.disable({ emitEvent: false });
          }
        }
        this.isFeesLoading = false;
        this.checkLoadingState();
      },
      error: (error) => {
        console.error('Failed to load current fees:', error);
        this.isFeesLoading = false;
        this.checkLoadingState();
      }
    });
  }

  applyFeesBasedOnType(): void {
    const registrationType = this.clientForm.get('registrationType')?.value;

    // New client: do not pre-fill registration fee (including lifetime) until
    // the user picks a registration type — avoids showing a system lifetime
    // amount on an empty "Registration Type" field.
    if (this.newClient && !registrationType) {
      this.clientForm.patchValue(
        { registrationFee: null, renewalFee: 0 },
        { emitEvent: false },
      );
      return;
    }

    // Edit mode: getOneClientById may still be in flight — avoid patching fees
    // from an empty type (would hit the legacy fallback and wrong totals).
    if (!this.newClient && !registrationType) {
      return;
    }

    // Prefer the fee stored in MongoDB only while the registration type is
    // unchanged from what we loaded. If the admin switches Life Time ↔ One
    // Year, always re-apply the current system fee for the *new* type so
    // Registration & Renewal and Consolidated Income reports see the right
    // `registrationFee` after save.
    const registrationTypeUnchanged =
      this.originalRegistrationType != null &&
      String(this.originalRegistrationType).trim() !== '' &&
      registrationType === this.originalRegistrationType;

    const useClientFee =
      !this.newClient &&
      registrationTypeUnchanged &&
      this.clientRegistrationFee !== null &&
      this.clientRegistrationFee !== undefined;

    if (registrationType === 'Life Time') {
      const regFeeToSet = useClientFee
        ? this.clientRegistrationFee!
        : this.currentLifetimeRegistrationFee;
      this.clientForm.patchValue({
        registrationFee: regFeeToSet,
        renewalFee: 0,
      });
    } else if (registrationType === 'One Year') {
      const regFeeToSet = useClientFee
        ? this.clientRegistrationFee!
        : this.currentRegistrationFee;

      // For renewalFee: if editing and a latest renewal fee exists, prefer that value
      const renewalFeeToSet = this.newClient
        ? 0
        : (this.latestRenewalFee > 0 ? this.latestRenewalFee : this.currentRenewalFee);

      this.clientForm.patchValue({
        registrationFee: regFeeToSet,
        renewalFee: renewalFeeToSet,
      });
    } else {
      // Edit / legacy: unknown type — keep previous behaviour (lifetime as default).
      // New client with unexpected type: clear fee rather than guessing lifetime.
      if (this.newClient) {
        this.clientForm.patchValue(
          { registrationFee: null, renewalFee: 0 },
          { emitEvent: false },
        );
        return;
      }
      const regFeeToSet = useClientFee
        ? this.clientRegistrationFee!
        : this.currentLifetimeRegistrationFee;
      this.clientForm.patchValue({
        registrationFee: regFeeToSet,
        renewalFee: 0,
      });
    }
  }

  /**
   * Map a backend duplicate-error message to the right form control.
   */
  private setDuplicateError(message: string): void {
    if (!message) return;
    if (message === "Email is already existing!") {
      this.clientForm.get("email")?.setErrors({ duplicateEmail: true });
    } else if (message === "Contact number is already existing!") {
      this.clientForm.get("guardianContactNo")?.setErrors({ duplicateEmail: true });
    } else if (message === "Client NIC is already existing!") {
      this.clientForm.get("guardianNIC")?.setErrors({ duplicateEmail: true });
    } else if (message === "Registration number is already existing!") {
      this.clientForm.get("registrationNo")?.setErrors({ duplicate: true });
    } else if (message === "Non-registered client number is already existing!") {
      this.clientForm.get("nonRegNo")?.setErrors({ duplicate: true });
    } else if (message === "BHT Number is already existing!") {
      this.clientForm.get("bhtNumber")?.setErrors({ duplicate: true });
    }
  }

  private normalizePayloadIdentifiers(payload: Record<string, unknown>): boolean {
    const type = payload["clientType"] as string;
    try {
      if (type === CLIENT_TYPES.REGISTERED && payload["registrationNo"]) {
        payload["registrationNo"] = normalizeClientIdentifier(
          type,
          String(payload["registrationNo"]),
        );
      } else if (type === CLIENT_TYPES.UNREGISTERED && payload["nonRegNo"]) {
        payload["nonRegNo"] = normalizeClientIdentifier(
          type,
          String(payload["nonRegNo"]),
        );
      } else if (type === CLIENT_TYPES.INWARD && payload["bhtNumber"]) {
        payload["bhtNumber"] = normalizeClientIdentifier(
          type,
          String(payload["bhtNumber"]),
        );
      }
      return true;
    } catch {
      if (type === CLIENT_TYPES.REGISTERED) {
        this.clientForm
          .get("registrationNo")
          ?.setErrors({ invalidIdentifier: true });
      } else if (type === CLIENT_TYPES.UNREGISTERED) {
        this.clientForm.get("nonRegNo")?.setErrors({ invalidIdentifier: true });
      } else if (type === CLIENT_TYPES.INWARD) {
        this.clientForm.get("bhtNumber")?.setErrors({ invalidIdentifier: true });
      }
      return false;
    }
  }

  saveUser() {
    this.isLoading = true;
    if (this.clientForm.valid) {
      const payload = this.clientForm.getRawValue();

      if (!this.normalizePayloadIdentifiers(payload)) {
        this.isLoading = false;
        return;
      }

      // Renewals are recorded only via Client Single View → Record renewal (not this form).
      if (!this.newClient) {
        delete payload.renewalFee;
        delete payload.newRenewalFee;
        delete payload.renewalDateChanged;
        delete payload.renewalFeeChanged;
      }

      // Ensure registration fee is sent for income reports (disabled controls are in getRawValue).
      if (
        payload.clientType === CLIENT_TYPES.REGISTERED &&
        payload.registrationType
      ) {
        const regFee = payload.registrationFee;
        if (regFee != null && regFee !== "") {
          payload.registrationFee = Number(regFee);
        }
      }

      // For edit mode: if expiryDate field has a value, keep it (user may have changed it or it's from DB)
      // If empty and One Year type, let server calculate it from registrationDate
      if (!this.newClient && payload.registrationType === 'One Year') {
        const currentExpiryDate = this.clientForm.get('expiryDate')?.value;
        if (!currentExpiryDate) {
          // Remove expiryDate so server will calculate it
          delete payload.expiryDate;
        }
        // else: keep the expiryDate from the form (either DB value or user-modified value)
      }

      // For Life Time clients, explicitly set expiryDate to null
      if (payload.registrationType === 'Life Time') {
        payload.expiryDate = null;
      }

      // Strip irrelevant fields based on the chosen client type so the
      // backend doesn't see leftover values from other forms.
      const type = payload.clientType;
      if (type === CLIENT_TYPES.UNREGISTERED) {
        delete payload.registrationNo;
        delete payload.bhtNumber;
        delete payload.roomNumber;
        delete payload.meal;
        delete payload.roster;
        delete payload.inwardDate;
        delete payload.registrationType;
        delete payload.registrationFee;
        delete payload.renewalFee;
        delete payload.renewalDate;
        delete payload.expiryDate;
        delete payload.registrationDate;
        // Keep optional demographic/contact fields if present (soft-warn):
        // dateOfBirth, guardianNIC, email, age, currentCondition.
      } else if (type === CLIENT_TYPES.INWARD) {
        delete payload.registrationNo;
        delete payload.nonRegNo;
        delete payload.nursingType;
        delete payload.startingDate;
        delete payload.guardianAddress;
        delete payload.email;
        delete payload.guardianNIC;
        delete payload.dateOfBirth;
        delete payload.age;
        delete payload.ageMonths;
        delete payload.currentCondition;
        delete payload.registrationType;
        delete payload.registrationFee;
        delete payload.registrationDate;
        delete payload.renewalFee;
        delete payload.renewalDate;
        delete payload.expiryDate;
      } else {
        // Registered - strip the unregistered/inward-only fields
        delete payload.nonRegNo;
        delete payload.bhtNumber;
        delete payload.nursingType;
        delete payload.startingDate;
        delete payload.roomNumber;
        delete payload.meal;
        delete payload.roster;
        delete payload.inwardDate;
      }

      if (this.clientId) {
        this.clientService
          .updateClient(payload)
          .pipe(
            catchError((error) => {
              const message =
                error?.error?.message || error?.message || "Failed to update client.";
              this.setDuplicateError(message);
              this.isLoading = false;
              this.alerts
                .open(message, {
                  label: "Error",
                  appearance: "error",
                })
                .subscribe();
              return of(null);
            })
          )
          .subscribe((res: any) => {
            this.isLoading = false;
            if (res) {
              this.alerts
                .open("Client updated successfully", {
                  label: "Success",
                  appearance: "success",
                })
                .subscribe();
              this.router.navigateByUrl(this.returnUrl || "admin/client");
            }
          });
      } else {
        if (this.convertedFromClientId) {
          payload.convertedFromClientId = this.convertedFromClientId;
        }
        this.clientService
          .createClient(payload)
          .pipe(
            catchError((error) => {
              const message =
                error?.error?.message || error?.message || "Failed to create client.";
              this.setDuplicateError(message);
              this.isLoading = false;
              this.alerts
                .open(message, {
                  label: "Error",
                  appearance: "error",
                })
                .subscribe();
              return of(null);
            })
          )
          .subscribe((res: any) => {
            this.isLoading = false;
            if (res) {
              this.alerts
                .open("Client added successfully", {
                  label: "Success",
                  appearance: "success",
                })
                .subscribe();
              this.router.navigateByUrl("admin/client");
            }
          });
      }
    } else {
      const count = countInvalidFields(this.clientForm);
      focusFirstInvalid(this.clientForm);
      this.alerts
        .open(
          `${count} field${count === 1 ? "" : "s"} need attention before saving.`,
          { label: "Form incomplete", appearance: "warning" },
        )
        .subscribe();
      this.isLoading = false;
    }
  }
}
